import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const stripCC = (mobile, cc) => {
  if (!mobile) return "";
  if (cc && mobile.startsWith(cc)) return mobile.slice(cc.length);
  return mobile.replace(/^\+/, "");
};

const buildForm = (c) => ({
  name: c.name || "",
  country_code: c.country_code || "+91",
  mobile: stripCC(c.mobile, c.country_code || "+91"),
  company: c.company || "",
  customer_type: c.customer_type || "prospect",
  preferred_language: c.preferred_language || "hinglish",
});

export default function EditCustomerDialog({ open, onOpenChange, customer, meta, onSaved }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(buildForm(customer));
  const [loading, setLoading] = useState(false);
  const [dup, setDup] = useState(null);

  useEffect(() => { setForm(buildForm(customer)); setDup(null); }, [customer]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (force = false) => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile number are required");
      return;
    }
    setLoading(true);
    if (!force) setDup(null);
    try {
      const r = await api.patch(`/customers/${customer.id}`, { ...form, force });
      toast.success(force ? "Duplicate authorized and saved" : "Customer updated");
      onSaved?.(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.customer) setDup(detail.customer);
      else toast.error(formatApiError(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setDup(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit customer information</DialogTitle>
          <DialogDescription>Update the customer's name, contact number and profile.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Customer name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile number *</Label>
            <div className="flex gap-2">
              <Input value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="w-20" data-testid="edit-country-code-input" />
              <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="flex-1" inputMode="tel" data-testid="edit-mobile-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Company (optional)</Label>
            <Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="edit-company-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                <SelectTrigger data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="existing">Existing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Select value={form.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
                <SelectTrigger data-testid="edit-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.languages || []).map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}{l.recommended ? " ★" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {dup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="edit-duplicate-warning">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertCircle className="h-4 w-4" /> Another customer already has this number
              </div>
              <p className="mt-1 text-xs text-amber-700">{dup.name}{dup.company ? ` · ${dup.company}` : ""} — {dup.mobile}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" onClick={() => { onOpenChange(false); navigate(`/customers/${dup.id}`); }} data-testid="edit-open-existing-button">
                  Open that record
                </Button>
                {user.role === "admin" ? (
                  <Button size="sm" variant="destructive" onClick={() => submit(true)} disabled={loading} data-testid="edit-authorize-duplicate-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Authorize & save anyway"}
                  </Button>
                ) : (
                  <span className="self-center text-xs text-amber-700">Only an administrator can authorize a duplicate number.</span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => submit(false)} disabled={loading || !!dup} data-testid="edit-customer-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
